import { addPrognosis } from '@api/resident';
import React, { useEffect, useState, forwardRef } from 'react';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { SubmitStatus, SubmitStatusEnum } from '@typings/common';
import getErrorMessage from '@utils/errorUtils';
import ErrorSummary from '../../ErrorSummary';

const prognosisOptions = [
  {
    value: 'Year Plus',
    label: 'Year Plus',
  },
  {
    value: 'Months',
    label: 'Months',
  },
  { value: 'Weeks', label: 'Weeks' },
  {
    value: 'Days',
    label: 'Days',
  },
  { value: 'After Care', label: 'After Care' },
];

interface PrognosisProps {
  fullName: string;
  slug: string | undefined;
  prognosisFromData: string;
  isUpdatedPronosis: boolean;
  setIsUpdatedPrognosis: (value: boolean) => void;
}

const Prognosis = forwardRef<HTMLDivElement, PrognosisProps>(
  (
    {
      fullName,
      prognosisFromData,
      isUpdatedPronosis,
      setIsUpdatedPrognosis,
      slug,
    },
    ref
  ) => {
    const [selectedPrognosis, setSelecttedPrognosis] = useState('');
    const [savePrognosisStatus, setSavePrognosisStatus] =
      useState<SubmitStatus>({
        loading: false,
        submitted: SubmitStatusEnum.IDLE,
        message: '',
      });

    useEffect(() => {
      setSelecttedPrognosis(prognosisFromData ?? '');
    }, [prognosisFromData]);

    const handleSelectPrognosis = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      setSelecttedPrognosis(value);
      const handleAddPrognosis = async () => {
        if (slug) {
          try {
            const response = await addPrognosis(slug, value);
            if (response && response.message === 'success') {
              setSavePrognosisStatus(prev => ({
                ...prev,
                submitted: SubmitStatusEnum.SUCCESS,
                message: 'Prognosis has been updated',
              }));
              setIsUpdatedPrognosis(!isUpdatedPronosis);
              setTimeout(() => {
                setSavePrognosisStatus(prev => ({
                  ...prev,
                  submitted: SubmitStatusEnum.IDLE,
                  message: '',
                }));
              }, 3000);
            }
          } catch (error) {
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);

            setSavePrognosisStatus({
              loading: false,
              submitted: SubmitStatusEnum.FAILED,
              message: errorMessage,
            });
          } finally {
            setSavePrognosisStatus(prev => ({ ...prev, loading: false }));
          }
        }
      };
      handleAddPrognosis();
    };

    return (
      <div
        className="govuk-form-group mt-10 govuk-bp:14"
        ref={ref}
        id="pigStatusForm"
      >
        <h3 className="govuk-label-wrapper">
          <label
            className="govuk-label govuk-label--s"
            htmlFor="select-prognosis"
          >
            {fullName} - Current level of need (PIG)
          </label>
        </h3>
        <div id="select-prognosis-hint" className="govuk-hint">
          Update prognosis and current level of need
        </div>
        {savePrognosisStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title={savePrognosisStatus.message}
            duration={3000}
            noScrollToTop
          />
        )}
        {savePrognosisStatus.submitted === SubmitStatusEnum.FAILED && (
          <ErrorSummary
            errors={[
              { id: 'savePrognosis', message: savePrognosisStatus.message },
            ]}
          />
        )}
        <select
          className="govuk-select"
          id="select-prognosis"
          name="carehome-partner-list"
          value={selectedPrognosis}
          onChange={handleSelectPrognosis}
        >
          <option value="" disabled>
            Select
          </option>
          {prognosisOptions?.map(option => {
            return (
              <option
                value={option.value}
                className="govuk-header__link"
                key={option.value}
              >
                {option.label}
              </option>
            );
          })}
        </select>
      </div>
    );
  }
);

export default Prognosis;
